import logo from "../../assets/Revibelogo.png";
import { apiService } from "../../api/razorpay";
import { fetchUserSubscription } from "../../api/subscription";

// ─── Open Razorpay checkout popup ─────────────────────────────────────────────
export const openRazorpayCheckout = (razorpayOrder) =>
  new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      reject(
        new Error("Payment system not loaded. Please refresh and try again.")
      );
      return;
    }

    let userName = "Revibe User";
    let userEmail = "user@revibe.com";
    let userPhone = "9999999999";

    try {
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      userName = userData?.fullName || userData?.name || userName;
      userEmail = userData?.email || userEmail;
      userPhone = userData?.mobile || userData?.phone || userPhone;
    } catch (e) {
      console.error("Error parsing user data", e);
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY,
      amount: razorpayOrder.amount,
      currency: razorpayOrder.currency || "INR",
      name: "Revibe Wellness",
      description: "Subscription Payment",
      image: logo,
      order_id: razorpayOrder.id,
      prefill: { name: userName, email: userEmail, contact: userPhone },
      theme: { color: "#84cc16" },
      handler: (response) => resolve(response),
      modal: {
        ondismiss: () => reject(new Error("Payment cancelled by user")),
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  });

// ─── Save live subscription data to localStorage after payment ────────────────
const saveSubscriptionToLocalStorage = async () => {
  try {
    const subscription = await fetchUserSubscription();
    if (subscription) {
      localStorage.setItem("userSubscription", JSON.stringify(subscription));
      console.log("✅ Subscription saved to localStorage:", subscription);
    }
  } catch (err) {
    // Non-blocking: log but don't throw — payment already succeeded
    console.error("⚠️ Could not save subscription to localStorage:", err);
  }
};

// ─── Full payment flow: create order → open Razorpay → verify → save sub ──────
/**
 * @param {object} params
 * @param {string}      params.workoutId     - MongoDB _id of the workout/subscription
 * @param {string|null} params.dietId        - MongoDB _id of the diet add-on (optional)
 * @param {number}      params.finalTotal    - Amount in ₹ after discounts
 * @param {string|null} params.referralCode  - Applied referral code (or null)
 * @returns {Promise<object>} verifyResponse from the backend
 */
export const processPayment = async ({
  workoutId,
  dietId,
  finalTotal,
  referralCode,
}) => {
  const orderData = {
    amount: Math.round(finalTotal * 100), // paise
    currency: "INR",
    subscriptionId: workoutId,
    addons: dietId ? [dietId] : [],
    referralCode: referralCode || null,
  };

  const orderResponse = await apiService.createOrder(orderData);

  if (!orderResponse.razorpay_order_id) {
    throw new Error("Failed to create order.");
  }

  const razorpayOrder = {
    id: orderResponse.razorpay_order_id,
    amount: Math.round(finalTotal * 100), // paise
    currency: "INR",
  };

  const paymentResponse = await openRazorpayCheckout(razorpayOrder);

  const verifyResponse = await apiService.verifyPayment({
    razorpay_payment_id: paymentResponse.razorpay_payment_id,
    razorpay_order_id: paymentResponse.razorpay_order_id,
    razorpay_signature: paymentResponse.razorpay_signature,
  });

  if (!verifyResponse.success) {
    throw new Error("Payment verification failed.");
  }

  // ── Payment verified ✅ — now fetch & cache live subscription data ──────────
  await saveSubscriptionToLocalStorage();

  return verifyResponse;
};